import ClearAllIcon from '@mui/icons-material/ClearAll';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Box,
  Button,
  IconButton,
  Popover,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { IconHelper } from 'components/generic/IconHelper';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { Fragment, memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bloxValues } from 'themes/constants';
import { MMM_DD_YYYY_C_12HHMM } from 'utility/constants';
import { formatAssetString, formatDate } from 'utility/functions';
import { useSearchModalContext } from '../SearchModal';
import { EditorialAssetSearchResultItem } from 'utility/types/dataProvider';

/**
 * --------------------------------------------------------
 * Search Modal Footer - Editorial
 * - Selected count popover and accept buttons
 * --------------------------------------------------------
 */

export const SearchModalFooterEditorial = memo(() => {
  const { sessionConfig } = useCosmos();
  const siteTimeZone = sessionConfig?.time_zone;
  const theme = useTheme();
  const { t } = useTranslation();
  const { selections, setSelections, onAccept, clearSelections } =
    useSearchModalContext();

  const selectedCount = selections.length ?? 0;
  const hasNoSelections = selectedCount === 0;
  const strCountSelected = t('blox.general.count_selected', { count: selectedCount });
  const strSelectedAssets = t('blox.general.selected_assets');
  const strAccept = t('blox.actions.accept');
  const strClearAll = t('blox.actions.clear_all');
  const strRemove = t('blox.actions.remove');
  const strClose = t('blox.actions.close');
  const strStartTime = t('blox.editorial.asset.start_time.alt_label');

  // Selections popover state
  const popoverAnchorRef = useRef<HTMLButtonElement | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const handleOnAccept = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (!onAccept) return;
      onAccept(selections, event);
    },
    [onAccept, selections],
  );

  // Selected assets popover handlers
  const handleOpenPopover = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setPopoverOpen(true);
  }, []);

  const handleClosePopover = useCallback(() => {
    setPopoverOpen(false);
  }, []);

  const handleRemoveItem = useCallback(
    (item: EditorialAssetSearchResultItem) => {
      setSelections((prevItems) => {
        const selectionId = item?.uuid ?? '';
        const updated = prevItems.filter((item) => item.uuid !== selectionId);
        // Close popover if removing the last item
        if (updated.length === 0) {
          setPopoverOpen(false);
        }
        return updated;
      });
    },
    [setSelections],
  );

  const handleClearAllItems = useCallback(() => {
    clearSelections();
    setPopoverOpen(false);
  }, [clearSelections]);

  return (
    <Fragment>
      <Box
        id="search-modal-footer-editorial"
        data-testid="search-modal-footer-editorial"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          alignItems: 'center',
          width: '100%',
          gap: 2,
        }}
      >
        <Button
          ref={popoverAnchorRef}
          data-testid="search-modal-footer-selections-button"
          variant="text"
          color="info"
          size="medium"
          onClick={handleOpenPopover}
          disabled={hasNoSelections}
          aria-haspopup="true"
          aria-expanded={popoverOpen ? 'true' : undefined}
          aria-controls={popoverOpen ? 'search-modal-selections-popover' : undefined}
          sx={{
            width: '100px',
          }}
        >
          {strCountSelected}
        </Button>

        <Button
          data-testid="search-modal-accept-selections-button"
          variant="contained"
          color="primary"
          size="medium"
          onClick={handleOnAccept}
          disableElevation={true}
          disabled={false}
          sx={{
            width: '100px',
          }}
        >
          {strAccept}
        </Button>
      </Box>

      <Popover
        id="search-modal-selections-popover"
        data-testid="search-modal-selections-popover"
        anchorEl={popoverAnchorRef.current}
        open={popoverOpen}
        onClose={handleClosePopover}
        keepMounted={false}
        disablePortal={false}
        disableEnforceFocus={true}
        elevation={3}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              display: 'grid',
              gridTemplateRows: 'auto 1fr',
              gridTemplateAreas: `"selectheader" "selectlist"`,
              overflow: 'hidden',
              width: 400,
              maxHeight: 500,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[3],
            },
          },
          transition: {
            timeout: {
              enter: 0,
              exit: 0,
            },
            in: popoverOpen,
            unmountOnExit: true,
            appear: true,
          },
        }}
      >
        <Box
          data-testid="search-modal-selections-header"
          sx={{
            gridArea: 'selectheader',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${theme.palette.divider}`,
            px: 1,
            py: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: bloxValues.smallFontSizeRem,
              fontWeight: bloxValues.fontWeightMedium,
              color: theme.palette.text.secondary,
            }}
          >
            {strSelectedAssets} ({selectedCount})
          </Typography>

          <Box
            data-testid="search-modal-selections-header-actions"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Tooltip title={strClearAll}>
              <IconButton
                data-testid="search-modal-clear-all-selections-button"
                size="small"
                onClick={handleClearAllItems}
                disabled={hasNoSelections}
                sx={{
                  padding: 0.5,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    color: theme.palette.text.primary,
                    bgcolor: theme.palette.action.hover,
                  },
                }}
              >
                <ClearAllIcon
                  sx={{
                    fontSize: bloxValues.iconSizeRegular,
                  }}
                />
              </IconButton>
            </Tooltip>

            <Tooltip title={strClose}>
              <IconButton
                data-testid="search-modal-selections-popover-close-button"
                size="small"
                onClick={handleClosePopover}
                sx={{
                  padding: 0.5,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    color: theme.palette.text.primary,
                    bgcolor: theme.palette.action.hover,
                  },
                }}
              >
                <RemoveIcon
                  sx={{
                    fontSize: bloxValues.iconSizeRegular,
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box
          data-testid="search-modal-selections-list-container"
          sx={{
            gridArea: 'selectlist',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            p: 0.5,
            gap: 0.5,
          }}
        >
          {selections.map((item) => {
            const itemUuid = item?.uuid ?? '';
            const reactKey = `selection-${itemUuid}`;
            const itemTitle = item?.title ?? '';
            const itemType = item?.type ?? '';
            const typeFmt = formatAssetString(itemType, t) || '';
            const itemStartTime = item?.start_time ?? '';
            const itemTooltipTxt = `${typeFmt} - ${itemTitle}`;
            const fmtStartTime = formatDate({
              date: itemStartTime,
              shiftTz: siteTimeZone,
              format: MMM_DD_YYYY_C_12HHMM,
            });
            const startTimeStr = `${strStartTime}: ${fmtStartTime}`;
            return (
              <Box
                key={reactKey}
                data-testid={`search-modal-selection-item-${itemUuid}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 0.5,
                  p: 0.5,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.action.hover}`,
                  bgcolor: theme.palette.background.paper,
                  '&:hover': {
                    bgcolor: theme.palette.action.hover,
                  },
                }}
              >
                <IconHelper
                  iconType={itemType}
                  svgIconProps={{ style: { fontSize: 16 } }}
                />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minWidth: 0,
                    gap: 0.25,
                  }}
                >
                  <Typography
                    data-testid="search-modal-selection-item-title"
                    title={itemTooltipTxt}
                    sx={{
                      fontSize: bloxValues.smallFontSizeRem,
                      color: theme.palette.text.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {itemTitle}
                  </Typography>

                  <Typography
                    data-testid="search-modal-selection-item-start-time"
                    title={startTimeStr}
                    sx={{
                      fontSize: bloxValues.tinyFontSizeRem,
                      color: theme.palette.text.secondary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {fmtStartTime}
                  </Typography>
                </Box>

                <IconButton
                  data-testid="search-modal-selection-item-remove-button"
                  size="small"
                  onClick={() => handleRemoveItem(item)}
                  title={strRemove}
                  sx={{
                    padding: '4px',
                    flexShrink: 0,
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      color: theme.palette.error.main,
                      bgcolor: theme.palette.action.hover,
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      </Popover>
    </Fragment>
  );
});
SearchModalFooterEditorial.displayName = 'SearchModalFooterEditorial';
